/**
 * TalaDraw map styles — Protomaps schema (Mar 2026).
 *
 * We host our own PMTiles archive on Cloudflare R2 (Caribbean region,
 * ~657 MB, zoom 0-15) generated from Protomaps' daily planet build. The
 * schema (layer names + field names) matches Protomaps' basemap layers:
 * `earth`, `landuse`, `landcover`, `water`, `roads`, `buildings`,
 * `boundaries`, `places`, `pois`.
 *
 * The `protomaps-themes-base` package exposes a `layers(source, key, lang)`
 * function that generates a full LayerSpecification[] array matching the
 * schema. We wrap it in a MapLibre style spec with our own source pointing
 * at the R2-hosted PMTiles, then patch in the TalaDraw neon-green accent
 * on motorways.
 */
import layers from "protomaps-themes-base";

// R2-hosted PMTiles archive URL. `pmtiles://` prefix triggers MapLibre's
// registered pmtiles protocol handler (see MapPage.js) which does ranged
// HTTP reads over HTTPS transparently.
const PMTILES_URL = "pmtiles://https://pub-9f755a93108e40f397de0bb7fd421ed6.r2.dev/caribbean.pmtiles";

// Source name — Protomaps' `layers()` fn returns layers pointing at whatever
// source name we pass in; the source object below has to use the same key.
const SOURCE_NAME = "protomaps";

// Sprites + glyphs hosted by Protomaps on GitHub Pages (free, versioned).
// The theme-specific sprite is baked into the corresponding layer refs.
const DARK_SPRITE = "https://protomaps.github.io/basemaps-assets/sprites/v4/dark";
const LIGHT_SPRITE = "https://protomaps.github.io/basemaps-assets/sprites/v4/light";
const GLYPHS_URL = "https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf";

const ATTRIBUTION =
  '<a href="https://github.com/protomaps/basemaps">Protomaps</a> © <a href="https://openstreetmap.org">OpenStreetMap</a>';

// TalaDraw brand accents.
const BRAND_GREEN_DARK = "#00FF94";
const BRAND_GREEN_LIGHT = "#00CC77";

/**
 * Apply TalaDraw's neon-green highway accent onto the generated layer array.
 * Protomaps stores road classification in the `kind` field of the `roads`
 * layer; the theme's layer IDs include the road tier as a suffix (e.g.
 * `roads_highway`, `roads_major_road`). We recolour anything explicitly
 * called out as a highway so users get instant motorway wayfinding in
 * our brand colour.
 */
function applyBrandAccent(layerArray, brandGreen) {
  return layerArray.map((layer) => {
    if (
      layer.type === "line" &&
      layer["source-layer"] === "roads" &&
      (layer.id || "").includes("highway")
    ) {
      const patched = { ...layer, paint: { ...(layer.paint || {}) } };
      patched.paint["line-color"] = brandGreen;
      if (patched.paint["line-opacity"] === undefined) {
        patched.paint["line-opacity"] = 0.9;
      }
      return patched;
    }
    return layer;
  });
}

function buildStyle(themeKey, brandGreen, sprite) {
  const themeLayers = layers(SOURCE_NAME, themeKey, "en");
  return {
    version: 8,
    name: `TalaDraw ${themeKey}`,
    sprite,
    glyphs: GLYPHS_URL,
    sources: {
      [SOURCE_NAME]: {
        type: "vector",
        url: PMTILES_URL,
        attribution: ATTRIBUTION,
      },
    },
    layers: applyBrandAccent(themeLayers, brandGreen),
  };
}

export const darkStyle = buildStyle("dark", BRAND_GREEN_DARK, DARK_SPRITE);
export const lightStyle = buildStyle("light", BRAND_GREEN_LIGHT, LIGHT_SPRITE);

export const MAP_STYLES = {
  dark: darkStyle,
  light: lightStyle,
};

export function getMapStyle(name) {
  return MAP_STYLES[name] || darkStyle;
}
